import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
  type WorkerRequirementDomain,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { useFilterBackgroundCheckRequirements } from "@src/appV2/redesign/BackgroundChecks/useFilterBackgroundCheckRequirements";
import { type AxiosError } from "axios";

import { type WorkplaceDocumentsPostBookingRequirementsFilter } from "../../paths";
import { isPostBookingRequirementForShift } from "../../utils/isPostBookingRequirementForShift";
import { mergeWorkerRequirementWithIncludedResources } from "../mergeWorkerRequirementWithIncludedResources";
import { type WorkerRequirement } from "../types";
import { requirementRelationship, useGetWorkerRequirements } from "../useGetWorkerRequirements";

interface UseGetRequirementsForWorkplaceProps {
  qualifications: string[];
  workplaceId: string;
  domain?: WorkerRequirementDomain;
  postBookingRequirementsFilter?: WorkplaceDocumentsPostBookingRequirementsFilter;
}

export function useGetRequirementsForWorkplace(
  props: UseGetRequirementsForWorkplaceProps,
  options: UseGetQueryOptions<GetWorkerRequirementsResponse, AxiosError, WorkerRequirement[]> = {}
) {
  const { qualifications, workplaceId, domain, postBookingRequirementsFilter } = props;

  const { filterOutBackgroundCheckRequirements } = useFilterBackgroundCheckRequirements();

  return useGetWorkerRequirements(
    {
      filter: {
        workplaceIds: [workplaceId],
        qualifications,
        requiredForBooking: true,
        visibleToWorker: true,
        ...(domain ? { domains: [domain] } : {}),
      },
      include: [...requirementRelationship, API_TYPES.requirementMappings],
    },
    {
      select: (data) => {
        const mergedData = mergeWorkerRequirementWithIncludedResources(data);

        // ACT-3205: When run-background-check-on-booking is enabled, we hide "missing" and "expired" UI for Checkr background checks.
        // If background check is MISSING or EXPIRED, we want to hide it (filter it out) as we don't want the worker to know
        // about it since they cannot take an action on it (as it runs automatically on booking). However, if it is
        // MISSING or EXPIRED but worker was auto-unassigned, we don't want to hide it as we will relay other information to
        // the worker about it.
        const filteredData = filterOutBackgroundCheckRequirements({
          workerRequirements: mergedData,
          filter: {
            status: [WorkerRequirementStatus.MISSING, WorkerRequirementStatus.EXPIRED],
          },
        });

        switch (postBookingRequirementsFilter) {
          case "exclude":
            return filteredData.filter(
              (requirement) =>
                !isPostBookingRequirementForShift({ requirement, workplaceId, qualifications })
            );
          case "include-only":
            return filteredData.filter((requirement) =>
              isPostBookingRequirementForShift({ requirement, workplaceId, qualifications })
            );
          default:
            return filteredData;
        }
      },
      ...options,
    }
  );
}
